import { ChecklistenEingabe } from '../components/ChecklistenEingabe'
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useAufforderungen } from '../hooks/useAufforderungen'
import { ChecklistZeile } from '../components/ChecklistZeile'
import { useAlleOk } from '../components/AlleOk'
import { PageHeader, Card, Button, Badge, Select, useEinsatz, useEinsatzWechsel, useConnectionRetry, aktuellePhase, phaseDerChecklist, phaseIndex, phaseLabel, checklistenAnzeigeName, checklistenFortschritt } from '@pilot-hub/ui'
import { KeinEinsatzLeer } from '../components/LeerZustand'
import { PhasenAnzeige } from '../components/PhasenAnzeige'
import { api, flightApi, checklistenApi } from '@pilot-hub/api-client'
import { FlugCrewKarte } from '../components/FlugCrewKarte'
import { MeinTrupp } from '../components/MeinTrupp'
import WeatherCard, { WeatherData } from '../components/WeatherCard'
import { Play, Square, RotateCcw, CheckSquare, ChevronDown, ChevronUp, Loader2, AlertTriangle, Plane, Check, X } from 'lucide-react'

/** Wert eines Zahlenfelds: nur Zahlen anzeigen (ältere Einträge enthalten "true" aus „Alle Pflichtfelder ausfüllen“) */
const zahlWert = (w: string | null | undefined) => (w && Number.isFinite(Number(w)) ? w : '')

// Types
interface ChecklistenItem {
  id: number
  instanz_id: number
  vorlage_item_id: number | null
  text: string
  typ: 'CHECK' | 'TEXT' | 'NUMBER' | 'PHOTO'
  pflicht: boolean
  sortierung: number
  hinweis: string | null
  wert: string | null
  ausgefuellt_am: string | null
  ausgefuellt_von: number | null
}

interface ChecklistenInstanz {
  id: number
  einsatz_id: number
  flug_id: number | null
  vorlage_id: number | null
  name: string
  phase: string
  modul: string
  status: string
  erstellt_am: string
  abgeschlossen_am: string | null
  abgeschlossen_von: number | null
  items: ChecklistenItem[]
}

interface Flug {
  id: number
  flug_nr: string
  drohne_label?: string | null
  akku_label?: string | null
  einsatz_titel?: string | null
  startzeit: string | null
  endzeit: string | null
  einsatz_id: number
  dji_flug?: boolean
}

interface Einsatz {
  id: number
  titel: string
  status: string
  typ?: string
  einsatzort_adresse?: string | null
  einsatzort_lat?: number | null
  einsatzort_lon?: number | null
}

const PILOT_PHASES_CO_PILOT = ['START', 'LANDUNG'] as const
const EINSATZ_PHASE_PILOT = 'EINSATZENDE_PILOT'

export function CoPilotChecklistPage() {
  const [fluege, setFluege] = useState<Flug[]>([])
  const { frage: frageAlleOk, dialog: alleOkDialog } = useAlleOk()
  // Einsatz kommt aus der globalen Einsatzwahl (Co-Pilot: Einsatz aus dem Kräfte Management, gesperrt)
  const { einsatz: ew, einsatzId, loading: ewLoading, fehler: ewFehler } = useEinsatz()
  const einsatz = useMemo(() => (ew?.raw ?? null) as Einsatz | null, [ew?.id]) // eslint-disable-line react-hooks/exhaustive-deps -- Poll liefert neue Objekte; nur bei anderem Einsatz neu
  const [selectedFlugId, setSelectedFlugId] = useState<string>('')
  const [checklisten, setChecklisten] = useState<ChecklistenInstanz[]>([])
  const [loading, setLoading] = useState(true)
  const [fehler, setFehler] = useState<string | null>(null)
  // Nachflug flow state
  const [showNachflugModal, setShowNachflugModal] = useState(false)
  const [nachflugInstanz, setNachflugInstanz] = useState<ChecklistenInstanz | null>(null)

  // Wetter-State (immer sichtbar, sobald aktiver Einsatz vorhanden)
  const [weatherData, setWeatherData] = useState<WeatherData | null>(null)
  const [weatherHinweis, setWeatherHinweis] = useState<string | null>(null)
  const [weatherNonce, setWeatherNonce] = useState(0)

  // Koordinaten direkt aus dem aktuellen Einsatz (Poll liefert sie ggf. erst nachträglich, z. B. wenn die Führung den Ort ergänzt)
  const rawEinsatz = ew?.raw as Einsatz | undefined
  const wetterLat = rawEinsatz?.einsatzort_lat ?? null
  const wetterLon = rawEinsatz?.einsatzort_lon ?? null
  const wetterAdresse = rawEinsatz?.einsatzort_adresse ?? null

  const ladenWetter = useCallback(async (lat: number, lon: number, abbruch: { ab: boolean }) => {
    try {
      // Server-Endpunkt: Open-Meteo, Ausweichquelle DWD (Bright Sky), zuletzt bekannte Werte; Kp-Index ebenfalls serverseitig.
      // Wiederholungen bei kurzer Störung (503 des Servers = alle Quellen gestört)
      let daten: WeatherData | null = null
      for (let versuch = 0; versuch < 3 && !daten; versuch++) {
        if (versuch > 0) await new Promise(r => setTimeout(r, versuch * 3000))
        if (abbruch.ab) return
        try {
          daten = (await api.get('/wetter', { params: { lat, lon }, timeout: 20000 })).data as WeatherData
        } catch (e) { if (versuch === 2) throw e }
      }
      if (abbruch.ab || !daten) return
      setWeatherData(daten)
      setWeatherHinweis(null)
    } catch (e) {
      console.error('Wetter laden fehlgeschlagen:', e)
      if (!abbruch.ab) setWeatherHinweis('Wetterdaten konnten nicht geladen werden (keine Wetterquelle erreichbar).')
    }
  }, [])

  // Wetter laden, sobald ein aktiver Einsatz vorliegt: mit Koordinaten direkt, sonst über die Adresse (Nominatim)
  useEffect(() => {
    const abbruch = { ab: false }
    if (!rawEinsatz) { setWeatherData(null); setWeatherHinweis(null); return () => { abbruch.ab = true } }
    setWeatherHinweis(null)
    ;(async () => {
      let lat = wetterLat, lon = wetterLon
      if (lat == null || lon == null) {
        if (!wetterAdresse) { setWeatherData(null); setWeatherHinweis('Für diesen Einsatz ist kein Einsatzort hinterlegt – ohne Ort gibt es keine Wetterdaten.'); return }
        try {
          const r = await fetch(`https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(wetterAdresse)}`, { headers: { 'Accept-Language': 'de' }, signal: AbortSignal.timeout(12000) })
          const j = await r.json()
          lat = j?.[0] ? Number(j[0].lat) : null
          lon = j?.[0] ? Number(j[0].lon) : null
        } catch { /* unten Hinweis */ }
        if (lat == null || lon == null || !Number.isFinite(lat) || !Number.isFinite(lon)) {
          if (!abbruch.ab) { setWeatherData(null); setWeatherHinweis('Der Einsatzort hat keine Koordinaten und die Adresse wurde nicht gefunden – bitte Einsatzort in der Führung auf der Karte setzen.') }
          return
        }
      }
      await ladenWetter(lat, lon, abbruch)
    })()
    return () => { abbruch.ab = true }
  }, [!!rawEinsatz, wetterLat, wetterLon, wetterAdresse, weatherNonce, ladenWetter]) // eslint-disable-line react-hooks/exhaustive-deps

  const laden = useCallback(async () => {
    setLoading(true)
    setFehler(null)
    try {
      const einsatzData = einsatz

      if (!einsatzData) {
        setFluege([])
        setChecklisten([])
        return
      }

      // Aktive Flüge für diesen Einsatz laden
      // alle laufenden Flüge des Einsatzes (nicht nur die, in denen man selbst Pilot ist)
      const fluegeRes = await flightApi.aktive({ einsatz_id: einsatzData.id })
      const allFluege = (fluegeRes.data as Flug[]) ?? []
      // Nur Flüge dieses Einsatzes
      const einsatzFluege = allFluege.filter(f => f.einsatz_id === einsatzData.id)
      setFluege(einsatzFluege)

      // Auto-Select: wenn nur ein Flug, diesen nehmen
      if (einsatzFluege.length === 1) {
        setSelectedFlugId(String(einsatzFluege[0].id))
      } else if (!selectedFlugId && einsatzFluege.length > 0) {
        // Neuesten Flug als Default (letzter in der Liste = jüngster)
        const neuesterFlug = einsatzFluege.reduce((a, b) => 
          new Date(b.startzeit || 0) > new Date(a.startzeit || 0) ? b : a
        )
        setSelectedFlugId(String(neuesterFlug.id))
      }

      // Checklisten für diesen Einsatz laden
      const clRes = await checklistenApi.getEinsatzChecklisten(einsatzData.id)
      const allChecklisten = (clRes.data as any[]) ?? []
      // Nur PILOT-Checklisten für Co-Pilot (Start, Landung, Einsatzende_Pilot)
      // FUEHRUNG-Checklisten komplett ausblenden
      const pilotChecklisten = allChecklisten.filter(c => c.modul === 'PILOT')
      setChecklisten(pilotChecklisten)

    } catch (err: any) {
      setFehler(err?.response?.data?.detail || 'Daten konnten nicht geladen werden')
    } finally {
      setLoading(false)
    }
  }, [einsatz, selectedFlugId])

  useEffect(() => { if (!ewLoading) laden() }, [laden, ewLoading])
  useConnectionRetry(() => { laden() })
  // Einsatz gewechselt: Flugauswahl verwerfen
  useEffect(() => { setSelectedFlugId('') }, [einsatzId])
  // Jeder Schritt wird sofort gespeichert; nur ein offenes Nachflug-Fenster blockiert den Wechsel
  useEinsatzWechsel(() => !showNachflugModal)

  // Flug-Checklisten (rechte Spalte): Start, Landung und nachzutragende Vorflugkontrolle (Cloud-Flug) der laufenden
  // Flüge, jeweils nur solange sie noch offen sind. Nachflug gehört dem Piloten.
  const { daten: auff } = useAufforderungen()
  const landungFlugIds = new Set(auff.landung.map(l => l.flug_id))
  const nachtragFlugIds = new Set(auff.vorflug_fehlt.filter(f => f.rolle === 'CO_PILOT').map(f => f.flug_id))
  const flugChecklisten = useMemo(() => {
    const laufend = new Map(fluege.filter(f => !f.endzeit).map(f => [f.id, f]))
    const rang = (c: ChecklistenInstanz) => (landungFlugIds.has(c.flug_id!) && c.phase === 'LANDUNG' ? 0 : nachtragFlugIds.has(c.flug_id!) && c.phase === 'VORFLUG' ? 1 : 2)
    return checklisten.filter(c =>
      c.modul === 'PILOT' && c.flug_id != null && laufend.has(c.flug_id) && c.status !== 'ABGESCHLOSSEN' &&
      (c.phase === 'START' || c.phase === 'LANDUNG' || (c.phase === 'VORFLUG' && laufend.get(c.flug_id)?.dji_flug))
    ).sort((a, b) => rang(a) - rang(b))
  }, [checklisten, fluege, auff]) // eslint-disable-line react-hooks/exhaustive-deps
  const dringend = flugChecklisten.some(c => (c.phase === 'LANDUNG' && landungFlugIds.has(c.flug_id!)) || (c.phase === 'VORFLUG' && nachtragFlugIds.has(c.flug_id!)))

  // Einsatz-Checklisten (einmal je Einsatz, gemeinsame Instanz für alle Co-Piloten): Einsatzbereitschaft, am Einsatzort, Einsatzende
  // modul='PILOT', flug_id leer; geordnet nach der Phase der Zeitleiste
  const einsatzTyp = ew?.raw?.typ as string | undefined
  const phase = aktuellePhase(ew?.raw)
  const einsatzListen = useMemo(() => checklisten
    .filter(c => c.modul === 'PILOT' && !c.flug_id && phaseDerChecklist(c.modul, c.phase))
    .sort((a, b) => phaseIndex(phaseDerChecklist(a.modul, a.phase), einsatzTyp) - phaseIndex(phaseDerChecklist(b.modul, b.phase), einsatzTyp)),
  [checklisten, einsatzTyp])
  const phaseIdx = phaseIndex(phase, einsatzTyp)
  // aktuelle Phase prominent oben; frühere Phasen (oder alle, solange keine Phase gesetzt ist) eingeklappt; kommende ausgeblendet
  // Übungen (UEBUNG) haben keine Phasenleiste: alle Einsatz-Checklisten stehen gleichwertig oben, nichts ist gesperrt
  const ohnePhasenleiste = einsatzTyp === 'UEBUNG'
  const aktuelleListen = ohnePhasenleiste ? einsatzListen : einsatzListen.filter(c => !!phase && phaseDerChecklist(c.modul, c.phase) === phase)
  const fruehereListen = ohnePhasenleiste ? [] : einsatzListen.filter(c => {
    const pk = phaseDerChecklist(c.modul, c.phase)
    return !phase || (phaseIndex(pk, einsatzTyp) < phaseIdx)
  })

  // Speichern: jeder Haken sofort sichtbar (optimistisch), Speicheraufrufe laufen nacheinander, und ein Neuladen
  // verwirft veraltete Antworten bzw. wartet, bis alles gespeichert ist (sonst überschreibt eine späte Antwort
  // den zweiten schnell gesetzten Haken und er wirkt "nicht gespeichert").
  const speicherAnz = useRef(0)
  const ladeSeq = useRef(0)
  const speicherKette = useRef<Promise<unknown>>(Promise.resolve())

  const neuLadenChecklisten = useCallback(async () => {
    if (!einsatz?.id) return
    const mein = ++ladeSeq.current
    const clRes = await checklistenApi.getEinsatzChecklisten(einsatz.id)
    if (mein !== ladeSeq.current || speicherAnz.current > 0) return
    setChecklisten(((clRes.data as any[]) ?? []).filter(c => c.modul === 'PILOT'))
  }, [einsatz?.id])

  // Gemeinsamer Stand: andere Co-Piloten haken dieselbe Instanz ab -> regelmäßig im Hintergrund nachladen
  // (auch neue Flüge, z. B. von der Cloud API erkannte)
  useEffect(() => {
    if (!einsatz?.id) return
    const t = window.setInterval(async () => {
      if (document.visibilityState !== 'visible' || speicherAnz.current > 0) return
      const a = document.activeElement
      if (a && (a.tagName === 'INPUT' || a.tagName === 'TEXTAREA')) return
      try {
        await neuLadenChecklisten()
        const fl = await flightApi.aktive({ einsatz_id: einsatz.id })
        setFluege(((fl.data as Flug[]) ?? []).filter(f => f.einsatz_id === einsatz.id))
      } catch { /* nächster Versuch */ }
    }, 20000)
    return () => window.clearInterval(t)
  }, [einsatz?.id, neuLadenChecklisten])

  const aktiveFluegeCount = fluege.filter(f => !f.endzeit).length

  const speichern = async (aenderungen: { instanzId: number; itemId: number; wert: string }[]) => {
    setFehler(null)
    setChecklisten(prev => prev.map(c => {
      const mine = aenderungen.filter(x => x.instanzId === c.id)
      return mine.length ? { ...c, items: c.items.map(i => { const x = mine.find(m => m.itemId === i.id); return x ? { ...i, wert: x.wert } : i }) } : c
    }))
    speicherAnz.current++
    const lauf = speicherKette.current.then(async () => {
      for (const x of aenderungen) await checklistenApi.updateInstanzItem(x.instanzId, x.itemId, { wert: x.wert })
    })
    speicherKette.current = lauf.catch(() => {})
    try {
      await lauf
    } catch (err: any) {
      setFehler(err?.response?.data?.detail || 'Speichern fehlgeschlagen')
    } finally {
      speicherAnz.current--
    }
    if (speicherAnz.current === 0) { try { await neuLadenChecklisten() } catch { /* nächster Versuch */ } }
  }

  // Item update
  const updateItem = (instanzId: number, itemId: number, wert: string) => speichern([{ instanzId, itemId, wert }])

  // Mark all Pflicht fields in a checkliste
  const markAllPflicht = (instanz: ChecklistenInstanz) => speichern(
    instanz.items.filter(i => i.typ === 'CHECK' && i.pflicht && (!i.wert || i.wert === '')).map(i => ({ instanzId: instanz.id, itemId: i.id, wert: 'true' })))

  // Handle flight end - show Nachflug modal
  const handleFlugEnde = async (flugId: number) => {
    // Find NACHFLUG checkliste for this flug
    const nachflugInstanzFound = checklisten.find(c => 
      c.flug_id === flugId && c.phase === 'NACHFLUG' && c.modul === 'PILOT'
    )
    if (nachflugInstanzFound) {
      setNachflugInstanz(nachflugInstanzFound)
      setShowNachflugModal(true)
    } else {
      setFehler('Keine Nachflug-Checkliste für diesen Flug gefunden')
    }
  }

  // Confirm Nachflug and end flight
  const confirmNachflug = async () => {
    if (!nachflugInstanz) return
    
    // Check all Pflicht fields filled
    const pflichtOffen = nachflugInstanz.items.filter(i => i.pflicht && (!i.wert || i.wert === '')).length
    if (pflichtOffen > 0) {
      setFehler(`Nachflug-Checkliste hat noch ${pflichtOffen} offene Pflichtfelder.`)
      return
    }

    try {
      // Flug dieser Nachflug-Checkliste beenden (stoppt die Zeit), Landeort per GPS, falls verfügbar
      const flugId = nachflugInstanz.flug_id ?? Number(selectedFlugId)
      if (fluege.some(f => f.id === flugId)) {
        const pos = await new Promise<GeolocationPosition | null>(res => {
          if (!navigator.geolocation) return res(null)
          navigator.geolocation.getCurrentPosition(res, () => res(null), { enableHighAccuracy: true, timeout: 6000, maximumAge: 30000 })
        })
        await flightApi.end(flugId, pos ? {
          zielort_lat: pos.coords.latitude, zielort_lon: pos.coords.longitude,
          zielort: `${pos.coords.latitude.toFixed(6)}, ${pos.coords.longitude.toFixed(6)}`,
        } : undefined)
      }
      
      // Mark Nachflug instanz as completed
      await checklistenApi.updateInstanz(nachflugInstanz.id, { status: 'ABGESCHLOSSEN' })
      
      // Reload
      await neuLadenChecklisten()
      await laden()
      
      setShowNachflugModal(false)
      setNachflugInstanz(null)
    } catch (err: any) {
      setFehler(err?.response?.data?.detail || 'Flug beenden fehlgeschlagen')
    }
  }

  // Checkliste abschließen (Start, Einsatzende …; Landung läuft über handleFlugEnde)
  const abschliessen = async (instanz: any) => {
    setFehler(null)
    try {
      await checklistenApi.updateInstanz(instanz.id, { status: 'ABGESCHLOSSEN' })
      await neuLadenChecklisten()
    } catch (err: any) {
      setFehler(err?.response?.data?.detail || 'Checkliste konnte nicht abgeschlossen werden')
    }
  }

  // Cancel Nachflug modal
  const cancelNachflug = () => {
    setShowNachflugModal(false)
    setNachflugInstanz(null)
  }

  // Fill all Pflicht in Nachflug modal
  const markAllNachflugPflicht = async () => {
    if (!nachflugInstanz) return
    await markAllPflicht(nachflugInstanz)
  }

  // Geöffnetes Nachflug-Fenster nach jedem Neuladen mit den gespeicherten Werten aktualisieren
  useEffect(() => {
    if (!nachflugInstanz) return
    const frisch = checklisten.find(c => c.id === nachflugInstanz.id)
    if (frisch && frisch !== nachflugInstanz) setNachflugInstanz(frisch)
  }, [checklisten])  // eslint-disable-line react-hooks/exhaustive-deps

  const getPhaseIcon = (phase: string) => {
    switch (phase) {
      case 'START': return Play
      case 'LANDUNG': return Square
      case 'EINSATZENDE_PILOT': return CheckSquare
      default: return Plane
    }
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <PageHeader title="" subtitle="Checklisten für aktuelle Flüge" />
        <div className="flex justify-center py-12">
          <Loader2 className="h-10 w-10 animate-spin text-primary-400" />
        </div>
      </div>
    )
  }

  // Nur wenn gar nichts geladen werden konnte die ganze Seite ersetzen; sonst Meldung oberhalb der Checklisten
  if ((fehler || ewFehler) && !einsatz) {
    return (
      <div className="space-y-6">
        <PageHeader title="" subtitle="Checklisten für aktuelle Flüge" />
        <Card className="p-6 border-red-500/30 bg-red-500/10">
          <p className="text-red-200">{fehler || ewFehler}</p>
        </Card>
      </div>
    )
  }

  if (!einsatz) {
    return (
      <KeinEinsatzLeer art="checklisten" />
    )
  }

  return (
    <div className="space-y-6">
      {alleOkDialog}
      <PageHeader
        title=""
        subtitle={`Einsatz: ${einsatz?.titel ?? ''} (${fluege.length} ${fluege.length === 1 ? 'laufender Flug' : 'laufende Flüge'})`}
      />

      {fehler && (
        <div className="flex items-center gap-2 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          <span>{fehler}</span>
        </div>
      )}

      {/* Phase des Einsatzes (von der Führung gesetzt) */}
      <PhasenAnzeige einsatz={ew?.raw} />

      {/* Mein Trupp (nur wenn ich im Einsatz in einem Trupp bin) */}
      <MeinTrupp einsatzId={einsatzId} />

      {/* Besetzung des aktiven Flugs (Pilot, Co-Pilot, Luftraumbeobachter), volle Breite */}
      <FlugCrewKarte einsatzId={einsatzId} fluege={fluege as any} />

      {/* Reihe: Einsatz-Checkliste der aktuellen Phase | Wetter | Flug-Checklisten (am Handy untereinander, Dringendes oben) */}
      <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-3" data-testid="co-reihe">
        <div className="space-y-4" data-testid="einsatz-checkliste-aktuell">
          {aktuelleListen.map(instanz => (
            <CoPilotChecklistenCard key={instanz.id} instanz={instanz} typ={einsatzTyp} isEinsatzende={true}
              onUpdateItem={updateItem} onMarkAllPflicht={markAllPflicht} onAbschliessen={abschliessen} />
          ))}
          {aktuelleListen.length === 0 && (
            <Card className="p-4">
              <h3 className="mb-1 font-semibold text-dark-text">Einsatz-Checkliste</h3>
              <p className="text-sm text-dark-text-muted">
                {ohnePhasenleiste ? 'Für diese Übung gibt es keine Einsatz-Checkliste.' : phase ? `Für die Phase „${phaseLabel(phase)}“ gibt es keine Einsatz-Checkliste.` : 'Noch keine Phase gesetzt.'}
              </p>
            </Card>
          )}
        </div>

        <div><WeatherCard data={weatherData} hinweis={weatherHinweis} onRetry={() => setWeatherNonce(n => n + 1)} className="h-full" /></div>

        <div id="flug-checklisten" className={`space-y-4 ${dringend ? 'order-first lg:order-none' : ''}`} data-testid="flug-checklisten">
          {fehler && (
            <div className="flex items-center gap-2 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              <span>{fehler}</span>
            </div>
          )}
          {flugChecklisten.map(instanz => {
            const flug = fluege.find(f => f.id === instanz.flug_id)
            return (
              <div key={instanz.id}>
                {(fluege.length > 1 || instanz.phase === 'VORFLUG') && (
                  <p className="mb-1 text-xs text-dark-text-muted">
                    {flug?.flug_nr}{flug?.drohne_label ? ` • ${flug.drohne_label}` : ''}
                    {instanz.phase === 'VORFLUG' ? ' – Vorflugkontrolle fehlt, nachtragen' : ''}
                  </p>
                )}
                <CoPilotChecklistenCard instanz={instanz} direkt={!!flug?.dji_flug}
                  onUpdateItem={updateItem} onMarkAllPflicht={markAllPflicht} onFlugEnde={handleFlugEnde} onAbschliessen={abschliessen} />
              </div>
            )
          })}
          {flugChecklisten.length === 0 && (
            <Card className="p-4">
              <h3 className="mb-1 font-semibold text-dark-text">Flug-Checklisten</h3>
              <p className="text-sm text-dark-text-muted">Keine offenen Flug-Checklisten{aktiveFluegeCount === 0 ? ' – derzeit kein laufender Flug' : ''}.</p>
            </Card>
          )}
        </div>
      </div>

      {/* frühere, erledigte Einsatzphasen eingeklappt unterhalb der Reihe */}
      {fruehereListen.length > 0 && (
        <div className="space-y-2" data-testid="einsatz-checklisten-frueher">
          {fruehereListen.map(instanz => (
            <EingeklappteChecklist key={instanz.id} instanz={instanz} typ={einsatzTyp}
              onUpdateItem={updateItem} onMarkAllPflicht={markAllPflicht} onAbschliessen={abschliessen} />
          ))}
        </div>
      )}

      {/* Nachflug Modal */}
      {showNachflugModal && nachflugInstanz && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-4xl max-h-[90vh] bg-dark-bg rounded-2xl border border-dark-border overflow-hidden flex flex-col animate-fade-in">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-dark-border bg-dark-bg-secondary">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-primary-400/20 flex items-center justify-center">
                  <Square className="h-6 w-6 text-primary-400" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-dark-text">Nachflug-Checkliste</h2>
                  <p className="text-sm text-dark-text-muted">Alle Pflichtfelder müssen vor dem Flugende ausgefüllt sein</p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {nachflugInstanz.items.map((item: any) => (
                <ChecklistZeile key={item.id} typ={item.typ} className="p-4 rounded-xl border border-dark-border bg-dark-bg-tertiary/50">
                  <div className="flex items-center gap-3">
                    {item.typ === 'CHECK' && (
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={item.wert === 'true'}
                          onChange={(e) => updateItem(nachflugInstanz!.id, item.id, e.target.checked ? 'true' : '')}
                          className="haken"
                        />
                      </div>
                    )}
                    {item.typ === 'TEXT' && (
                      <ChecklistenEingabe
                        type="text"
                        wert={item.wert || ''}
                        onSpeichern={(v) => updateItem(nachflugInstanz!.id, item.id, v)}
                        className="min-h-[48px] flex-1 px-3 py-2 rounded border border-dark-border bg-dark-bg text-dark-text text-base"
                        placeholder="Freitext..."
                      />
                    )}
                    {item.typ === 'NUMBER' && (
                      <ChecklistenEingabe
                        type="number"
                        wert={zahlWert(item.wert)}
                        onSpeichern={(v) => updateItem(nachflugInstanz!.id, item.id, v)}
                        className="min-h-[48px] w-32 px-3 py-2 rounded border border-dark-border bg-dark-bg text-dark-text text-base"
                        placeholder="Zahl"
                      />
                    )}
                    {item.typ === 'PHOTO' && (
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => { /* photo upload */ }}
                        className="min-h-[48px] px-3 py-2 rounded border border-dark-border bg-dark-bg text-dark-text text-sm"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className={`font-medium text-base ${item.pflicht ? 'text-dark-text' : 'text-dark-text-muted'}`}>
                        {item.text}
                        {item.pflicht && <span className="checklist-pflicht">Pflicht</span>}
                      </p>
                      {item.hinweis && <p className="text-sm text-dark-text-muted mt-1">{item.hinweis}</p>}
                    </div>
                  </div>
                </ChecklistZeile>
              ))}
            </div>

            {/* Footer Actions */}
            <div className="flex items-center justify-end gap-3 p-4 border-t border-dark-border bg-dark-bg-secondary">
              <Button variant="secondary" onClick={cancelNachflug} className="min-h-[48px] min-w-[120px]">
                Abbrechen
              </Button>
              <Button variant="secondary" onClick={async () => { if (await frageAlleOk(nachflugInstanz.items.filter((i: any) => i.typ === 'CHECK' && i.pflicht && !i.wert).length)) await markAllNachflugPflicht() }} className="min-h-[48px] min-w-[180px]">
                Alle Pflichtpunkte ok
              </Button>
              <Button variant="primary" onClick={confirmNachflug} className="min-h-[48px] min-w-[120px]">
                Bestätigen
              </Button>
            </div>
          </div>
        </div>
      )}

    </div>
  )
}

// Sub-Component für eine Checklisten-Karte
function CoPilotChecklistenCard({ 
  instanz, 
  typ,
  onUpdateItem, 
  isEinsatzende = false,
  onMarkAllPflicht,
  onFlugEnde,
  onAbschliessen,
  direkt = false,
  einklappbar = true
}: { 
  instanz: any
  typ?: string
  onUpdateItem: (instanzId: number, itemId: number, wert: string) => void
  isEinsatzende?: boolean
  onMarkAllPflicht?: (instanz: any) => void
  onFlugEnde?: (flugId: number) => void
  onAbschliessen?: (instanz: any) => void
  /** Cloud-Flug: Landung erkennt die Drohne selbst, die Checkliste wird nur abgeschlossen (kein Flug beenden) */
  direkt?: boolean
  /** false: Karte ist schon in einer einklappbaren Hülle (EingeklappteChecklist) – kein eigener Pfeil/Auto-Einklappen */
  einklappbar?: boolean
}) {
  const pflichtItems = instanz.items.filter((i: any) => i.pflicht)
  const erledigt = pflichtItems.filter((i: any) => i.wert && i.wert !== '').length
  const gesamt = pflichtItems.length
  const isComplete = gesamt > 0 && erledigt === gesamt
  const { frage: frageAlleOk, dialog: alleOkDialog } = useAlleOk()
  const isInProgress = gesamt > 0 && erledigt > 0 && erledigt < gesamt
  // Einklappen nur bei tatsächlich abgeschlossener (abgegebener) Checkliste, nicht schon wenn alle Punkte abgehakt sind
  const fertig = instanz.status === 'ABGESCHLOSSEN'
  const abgeschlossenUm = fertig && instanz.abgeschlossen_am
    ? new Date(instanz.abgeschlossen_am).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }) : ''

  // Einklappen: Beim Öffnen sind erledigte Checklisten eingeklappt; wird eine Checkliste während der Nutzung fertig,
  // klappt sie nach kurzer Verzögerung ein. Manuelles Auf-/Zuklappen hat danach Vorrang (kein Wieder-Zuklappen beim Refresh).
  const [eingeklappt, setEingeklappt] = useState(einklappbar && fertig)
  const vorherFertig = useRef(fertig)
  useEffect(() => {
    if (einklappbar && fertig && !vorherFertig.current) {
      vorherFertig.current = true
      const t = window.setTimeout(() => setEingeklappt(true), 600)
      return () => window.clearTimeout(t)
    }
    vorherFertig.current = fertig
  }, [fertig, einklappbar])

  return (
    <Card className="p-4">
      {alleOkDialog}
      <div className={`flex flex-wrap items-center justify-between gap-x-2 gap-y-1 ${eingeklappt ? '' : 'mb-4'}`}>
        <div className="flex min-w-0 flex-1 basis-40 items-center gap-2">
          {fertig
            ? <Check className="h-5 w-5 shrink-0 text-green-400" />
            : <span className={`w-2 h-2 shrink-0 rounded-full ${isComplete ? 'bg-green-400' : erledigt > 0 ? 'bg-amber-400' : 'bg-red-400'}`} />}
          <h3 className="min-w-0 break-words font-semibold text-dark-text">{(!instanz.flug_id && checklistenAnzeigeName(instanz.phase, typ)) || instanz.name}</h3>
        </div>
        <div className="flex shrink-0 items-center gap-2 whitespace-nowrap">
          <Badge variant={instanz.status === 'ABGESCHLOSSEN' || isComplete ? 'success' : erledigt > 0 ? 'warning' : 'danger'}>
            {fertig ? `Abgeschlossen${abgeschlossenUm ? ` ${abgeschlossenUm}` : ''}` : eingeklappt ? `Offen ${erledigt}/${gesamt}` : `${erledigt}/${gesamt} Pflicht`}
          </Badge>
          {einklappbar && <button
            type="button"
            onClick={() => setEingeklappt(e => !e)}
            aria-expanded={!eingeklappt}
            aria-label={eingeklappt ? 'Aufklappen' : 'Einklappen'}
            title={eingeklappt ? 'Aufklappen' : 'Einklappen'}
            data-testid="checkliste-toggle"
            className="flex h-11 w-11 items-center justify-center rounded-lg text-dark-text-muted hover:bg-dark-bg-tertiary hover:text-dark-text transition-colors"
          >
            <ChevronDown className={`h-5 w-5 transition-transform duration-200 ${eingeklappt ? '' : 'rotate-180'}`} />
          </button>}
        </div>
      </div>

      {!eingeklappt && <>
      <div className="space-y-2 max-h-[600px] overflow-y-auto">
        {instanz.items.map((item: any) => (
          <ChecklistZeile key={item.id} typ={item.typ} className="p-3 rounded-lg border border-dark-border bg-dark-bg-tertiary/50">
            <div className="flex items-center gap-3">
              {item.typ === 'CHECK' && (
                <input
                  type="checkbox"
                  checked={item.wert === 'true'}
                  onChange={(e) => onUpdateItem(instanz.id, item.id, e.target.checked ? 'true' : '')}
                  className="haken"
                />
              )}
              {item.typ === 'TEXT' && (
                <ChecklistenEingabe
                  type="text"
                  wert={item.wert || ''}
                  onSpeichern={(v) => onUpdateItem(instanz.id, item.id, v)}
                  className="min-h-[48px] flex-1 px-3 py-1.5 rounded border border-dark-border bg-dark-bg text-dark-text text-sm"
                  placeholder="Freitext..."
                />
              )}
              {item.typ === 'NUMBER' && (
                <ChecklistenEingabe
                  type="number"
                  wert={zahlWert(item.wert)}
                  onSpeichern={(v) => onUpdateItem(instanz.id, item.id, v)}
                  className="min-h-[48px] w-24 px-3 py-1.5 rounded border border-dark-border bg-dark-bg text-dark-text text-sm"
                  placeholder="Zahl"
                />
              )}
              {item.typ === 'PHOTO' && (
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => { /* photo upload */ }}
                  className="min-h-[48px] px-3 py-1.5 rounded border border-dark-border bg-dark-bg text-dark-text text-sm"
                />
              )}
              <div className="flex-1 min-w-0">
                <p className={`font-medium text-sm ${item.pflicht ? 'text-dark-text' : 'text-dark-text-muted'}`}>
                  {item.text}
                  {item.pflicht && <span className="checklist-pflicht">Pflicht</span>}
                </p>
                {item.hinweis && <p className="text-xs text-dark-text-muted mt-0.5">{item.hinweis}</p>}
                {item.wert && item.ausgefuellt_am && (
                  <p className="text-xs text-dark-text-muted mt-1">
                    Ausgefüllt: {new Date(item.ausgefuellt_am).toLocaleString('de-DE')}
                  </p>
                )}
              </div>
            </div>
          </ChecklistZeile>
        ))}
      </div>

      {instanz.status !== 'ABGESCHLOSSEN' && (
        <div className="sticky bottom-2 z-10 -mx-1 space-y-2 rounded-xl border border-dark-border bg-dark-bg-secondary/95 p-3 backdrop-blur">
          <button
            onClick={async () => { if (await frageAlleOk(instanz.items.filter((i: any) => i.typ === 'CHECK' && i.pflicht && !i.wert).length)) onMarkAllPflicht?.(instanz) }}
            className="w-full min-h-[48px] py-2 px-4 rounded-lg border border-primary-500/60 bg-primary-600/15 text-primary-100 font-medium hover:bg-primary-600/25 transition-colors"
          >
            Alle Pflichtpunkte ok
          </button>
          <button
            onClick={() => {
              // Landung: Flug beenden mit Nachflug-Checkliste; alle anderen (Start, Einsatzende): abschließen
              if (instanz.phase === 'LANDUNG' && !isEinsatzende && !direkt) onFlugEnde?.(instanz.flug_id!)
              else onAbschliessen?.(instanz)
            }}
            disabled={erledigt < gesamt}
            className="w-full min-h-[48px] py-2 px-4 rounded-lg bg-primary-600 text-white font-medium hover:bg-primary-700 disabled:bg-primary-600/50 disabled:cursor-not-allowed transition-colors"
          >
            {erledigt < gesamt ? 'Bitte erst alle Pflichtfelder ausfüllen' : 'Als abgeschlossen markieren'}
          </button>
        </div>
      )}
      </>}
    </Card>
  )
}

export default CoPilotChecklistPage
/** Frühere Phase: eingeklappt mit Häkchen (oder Hinweis auf offene Punkte); aufklappbar und weiter bearbeitbar */
function EingeklappteChecklist({ instanz, typ, ...rest }: {
  instanz: any
  typ?: string
  onUpdateItem: (instanzId: number, itemId: number, wert: string) => void
  onMarkAllPflicht?: (instanz: any) => void
  onAbschliessen?: (instanz: any) => void
}) {
  const [offen, setOffen] = useState(false)
  const f = checklistenFortschritt(instanz)
  return (
    <div className="rounded-xl border border-dark-border bg-dark-bg-tertiary/40">
      <button type="button" onClick={() => setOffen(o => !o)} aria-expanded={offen} className="flex min-h-[48px] w-full items-center justify-between gap-2 px-4 py-2 text-left">
        <span className="flex items-center gap-2 font-medium text-dark-text">
          {f.fertig ? <Check className="h-5 w-5 text-green-400" /> : <AlertTriangle className="h-5 w-5 text-amber-400" />}
          {checklistenAnzeigeName(instanz.phase, typ) || instanz.name}
        </span>
        <span className="flex items-center gap-2 text-sm text-dark-text-muted">
          {f.fertig ? 'erledigt' : `${f.offen} offen`}
          {offen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </span>
      </button>
      {offen && <div className="px-2 pb-2"><CoPilotChecklistenCard instanz={instanz} typ={typ} isEinsatzende={true} einklappbar={false} {...rest} /></div>}
    </div>
  )
}
